import { useState } from "react";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IPersonalSiteFilter, ITableColumn, IWorkspaceFilter } from "@interfaces/common.interfaces";
import { IReportSectionProps } from "components/reports-common/report-section/report-section.props";
import ReportSection from "components/reports-common/report-section/report-section";
import {
    IOrphanedWorkspacesInTenantReportDetails,
    IOrphanedWorkspacesInTenantReportItem,
    WorkspaceOwnerInvalidReason
} from "interfaces/reports/orphaned-workspaces-reports.interfaces";
import OrphanedWorkspacesInTenantReportSummary from "components/reports/orphaned-objects/orphaned-workspaces-in-tenant-report/orphaned-workspaces-in-tenant-report/orphaned-workspaces-in-tenant-report-summary";
import WorkspaceLinkCell from "components/reports-common/report-results-grid-cells/workspace-link-cell/workspace-link-cell";
import PrincipalsCell from "components/reports-common/report-results-grid-cells/principals-cell/principals-cell";
import StatusBadges from "components/reports-common/report-results-grid-cells/status-badges/status-badges";
import PersonalSiteFilter from "components/controls/personal-site-filter/personal-site-filter";
import { getSharePointTemplateKey, getWorkspaceTypeKey } from "lib/workspaces";
import { isWorkspaceFilterValid } from "lib/request-model-validation-helper";
import WorkspaceFilter from "components/controls/workspace-filter/workspace-filter";
import { IRequestModel } from "@interfaces/reports/reports-common.interfaces";

const reportUrl: string = "/api/reports/orphaned-workspaces-in-tenant";

export default function OrphanedWorkspacesInTenantReport() {
    const { t } = useL10n<ILocaleStrings>();
    const [model, setModel] = useState<IRequestModel>({});
    const [personalSiteFilter] = useState<IPersonalSiteFilter>({ includeWorkspaces: true, includeOneDrive: false });
    const [workspaceFilter, setWorkspaceFilter] = useState<IWorkspaceFilter>({
        applyFilter: false
    });

    // Helper function to get the locale key for invalid owner reasons
    const getOwnerInvalidReasonKey = (reason: WorkspaceOwnerInvalidReason): keyof ILocaleStrings => {
        switch (reason) {
            case WorkspaceOwnerInvalidReason.NotInDirectory:
                return "ppaOwnerInvalidNotInDirectory";
            case WorkspaceOwnerInvalidReason.AccountDisabled:
                return "ppaOwnerInvalidAccountDisabled";
            case WorkspaceOwnerInvalidReason.NoLicense:
                return "ppaOwnerInvalidNoLicense";
            default:
                return "unknown";
        }
    };

    const cols: Array<ITableColumn<IOrphanedWorkspacesInTenantReportItem>> = [
        {
            key: "workspaceName",
            label: t("ppaWorkspace"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 200,
            renderCell: (key: string, item: IOrphanedWorkspacesInTenantReportItem) => (
                <WorkspaceLinkCell
                    name={item.workspaceName}
                    url={item.workspaceUrl}
                    template={item.workspaceTemplate}
                    type={item.workspaceType}
                />
            ),
            getExportValue: (key: string, item: IOrphanedWorkspacesInTenantReportItem) => {
                return item.workspaceName;
            }
        },
        {
            key: "workspaceUrl",
            label: t("ppaWorkspaceUrl"),
            showInGrid: false,
            showInExport: true
        },
        {
            key: "workspaceType",
            label: t("ppaWorkspaceType"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 150,
            renderCell: (key: string, item: IOrphanedWorkspacesInTenantReportItem) => {
                const templateKey = getWorkspaceTypeKey(item.workspaceType);
                if (templateKey !== "unknown") {
                    return t(templateKey);
                }
                return item.workspaceType;
            },
            getExportValue: (key: string, item: IOrphanedWorkspacesInTenantReportItem) => {
                const templateKey = getWorkspaceTypeKey(item.workspaceType);
                if (templateKey !== "unknown") {
                    return t(templateKey);
                }
                const spTemplateKey = getSharePointTemplateKey(item.workspaceTemplate);
                if (spTemplateKey !== "unknown") {
                    return t(spTemplateKey);
                }
                return item.workspaceType;
            }
        },
        {
            key: "workspaceTemplate",
            label: t("ppaWorkspaceTemplate"),
            showInGrid: false,
            showInExport: true
        },
        {
            key: "owners",
            label: t("ppaOwners"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 200,
            renderCell: (key: string, item: IOrphanedWorkspacesInTenantReportItem) => {
                return <PrincipalsCell columnId={key} item={item} />;
            },
            getExportValue: (key: string, item: IOrphanedWorkspacesInTenantReportItem) => {
                if (!item.owners || item.owners.length === 0) {
                    return t("ppaNoOwners");
                }
                return item.owners.map(o => o.upn).join("; ");
            }
        },
        {
            key: "ownerStatus",
            label: t("ppaOwnerStatus"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 150,
            renderCell: (key: string, item: IOrphanedWorkspacesInTenantReportItem) => {
                if (!item.owners || item.owners.length === 0) {
                    return null;
                }
                const invalidReasons = item.owners
                    .filter(o => !o.isActiveOwner)
                    .map(o => o.reasonInvalid)
                    .filter((v, i, a) => a.indexOf(v) === i); // unique values

                if (invalidReasons.length === 0) {
                    return t("ppaAllOwnersValid");
                }

                const translatedReasons = invalidReasons.map(r => t(getOwnerInvalidReasonKey(r)));
                return <StatusBadges statuses={translatedReasons} />;
            },
            getExportValue: (key: string, item: IOrphanedWorkspacesInTenantReportItem) => {
                if (!item.owners || item.owners.length === 0) {
                    return t("ppaNoOwners");
                }
                return item.owners
                    .map(o => `${o.displayName} (${o.upn}): ${o.isActiveOwner ? t("ppaValid") : t(getOwnerInvalidReasonKey(o.reasonInvalid))}`)
                    .join("; ");
            }
        }
    ];

    const onPersonalSiteFilterChange = (value?: IPersonalSiteFilter) => {
        setModel({
            ...model,
            personalSiteFilter: {
                includeWorkspaces: value?.includeWorkspaces,
                includeOneDrive: value?.includeOneDrive
            }
        });
    }

    const onWorkspaceFilterChange = (value?: IWorkspaceFilter) => {
        setWorkspaceFilter({
            applyFilter: value?.applyFilter || false,
            filter: value?.filter
        });
        setModel({
            ...model,
            workspaceFilter: value?.applyFilter ?
                {
                    property: value?.filter?.property,
                    operator: value?.filter?.operator,
                    value: value?.filter?.value
                }
                : undefined
        });
    };

    // Model is valid if at least one of the properties is set to true.
    const isModelValid = (): boolean => {
        const hasPersonalSiteFilter = (model.personalSiteFilter?.includeWorkspaces || model.personalSiteFilter?.includeOneDrive) === true;

        return hasPersonalSiteFilter && isWorkspaceFilterValid(workspaceFilter.applyFilter, model.workspaceFilter);
    }

    const reportSectionProps: IReportSectionProps<IOrphanedWorkspacesInTenantReportItem, IOrphanedWorkspacesInTenantReportDetails> = {
        columns: cols,
        summaryComponent: <OrphanedWorkspacesInTenantReportSummary />,
        reportUrl: reportUrl,
        model: model,
        isModelValid: isModelValid(),
        noItemsMessage: t("ppaOrphanedWorkspacesInTenantReportNoItemsMessage"),
        defaultExportFilenamePrefix: "ppaOrphanedWorkspacesInTenantReportDefaultExportFilenamePrefix"
    }

    return (
        <ReportSection {...reportSectionProps}>
            <WorkspaceFilter model={workspaceFilter} onChange={onWorkspaceFilterChange} />
            <PersonalSiteFilter model={personalSiteFilter} onChange={onPersonalSiteFilterChange} />
        </ReportSection>
    );
}
